import { useState, useRef, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Upload, FileText, Download, Trash2, X, CheckCircle, Loader2, FileSpreadsheet,
} from "lucide-react";
import { Button } from "../ui/button";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const formatSize = (b) =>
  b < 1024 ? b + " B"
    : b < 1048576 ? (b / 1024).toFixed(1) + " KB"
    : (b / 1048576).toFixed(1) + " MB";

const VALID_EXTS = ["txt", "srt"];
const hasValidExt = (file) => VALID_EXTS.includes(file.name.split(".").pop().toLowerCase());

export const CriadorRoteiro = ({ getAuthHeaders }) => {
  const [files, setFiles] = useState([]);
  const [isDragging, setIsDragging] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [previewData, setPreviewData] = useState([]);
  const [processedFiles, setProcessedFiles] = useState([]);
  const fileInputRef = useRef(null);

  const handleDragOver = useCallback((e) => { e.preventDefault(); setIsDragging(true); }, []);
  const handleDragLeave = useCallback((e) => { e.preventDefault(); setIsDragging(false); }, []);

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setIsDragging(false);
    const dropped = Array.from(e.dataTransfer.files);
    const valid = dropped.filter(hasValidExt);
    if (valid.length !== dropped.length) toast.error("Apenas .txt e .srt permitidos");
    if (valid.length > 0) {
      setFiles(prev => [...prev, ...valid]);
      toast.success(`${valid.length} arquivo(s) adicionado(s)`);
    }
  }, []);

  const handleFileChange = (e) => {
    const valid = Array.from(e.target.files).filter(hasValidExt);
    if (valid.length > 0) {
      setFiles(prev => [...prev, ...valid]);
      toast.success(`${valid.length} arquivo(s) adicionado(s)`);
    }
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const buildFormData = () => {
    const fd = new FormData();
    files.forEach(f => fd.append("files", f));
    return fd;
  };

  const processFiles = async () => {
    if (files.length === 0) return toast.error("Adicione pelo menos um arquivo");
    setIsProcessing(true);
    try {
      const res = await axios.post(`${API}/tools/srt/upload`, buildFormData(), {
        headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" },
      });
      if (res.data.success) {
        setProcessedFiles(res.data.files);
        setPreviewData(res.data.lines || []);
        toast.success(`${res.data.total_lines} linhas processadas!`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao processar");
    } finally {
      setIsProcessing(false);
    }
  };

  const downloadExcel = async () => {
    if (files.length === 0) return toast.error("Adicione pelo menos um arquivo");
    setIsProcessing(true);
    try {
      const res = await axios.post(`${API}/tools/srt/generate-excel`, buildFormData(), {
        responseType: "blob",
        headers: { ...getAuthHeaders(), "Content-Type": "multipart/form-data" },
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.download = `legendas_${new Date().toISOString().slice(0, 10)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      setTimeout(() => { document.body.removeChild(link); window.URL.revokeObjectURL(url); }, 100);
      toast.success("Excel gerado!");
    } catch {
      toast.error("Erro ao gerar Excel");
    } finally {
      setIsProcessing(false);
    }
  };

  const clearFiles = () => { setFiles([]); setPreviewData([]); setProcessedFiles([]); };

  return (
    <div className="space-y-6" data-testid="criador-roteiro">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5 space-y-4">
          <div
            data-testid="srt-upload-zone"
            className={`border-2 border-dashed rounded-xl p-10 flex flex-col items-center justify-center cursor-pointer transition-all min-h-[220px] ${isDragging ? "border-teal-400 bg-teal-400/5" : "border-slate-700 hover:border-slate-500"}`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            onClick={() => fileInputRef.current?.click()}
          >
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".txt,.srt"
              onChange={handleFileChange}
              className="hidden"
              data-testid="srt-file-input"
            />
            <Upload className={`w-12 h-12 mb-3 ${isDragging ? "text-teal-400" : "text-slate-600"}`} />
            <p className="text-slate-400 text-sm text-center">
              Arraste arquivos .SRT / .TXT ou clique para selecionar
            </p>
          </div>

          {files.length > 0 && (
            <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4" data-testid="srt-file-list">
              <div className="flex justify-between items-center mb-3">
                <span className="text-sm text-slate-400">Arquivos ({files.length})</span>
                <button
                  onClick={clearFiles}
                  className="text-xs text-red-400 hover:text-red-300 flex items-center gap-1"
                  data-testid="srt-clear-files"
                >
                  <Trash2 className="w-3 h-3" /> Limpar
                </button>
              </div>
              <div className="max-h-[200px] overflow-y-auto space-y-2">
                {files.map((f, i) => (
                  <div
                    key={`${f.name}-${f.size}-${i}`}
                    className="flex items-center justify-between bg-slate-900/50 rounded-lg px-3 py-2"
                    data-testid={`srt-file-${i}`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText className="w-4 h-4 text-teal-400 shrink-0" />
                      <span className="text-sm text-white truncate">{f.name}</span>
                      <span className="text-xs text-slate-500">{formatSize(f.size)}</span>
                    </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); setFiles(prev => prev.filter((_, idx) => idx !== i)); }}
                      className="text-slate-500 hover:text-red-400 p-1"
                      data-testid={`srt-remove-${i}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex gap-3">
            <Button
              onClick={processFiles}
              disabled={files.length === 0 || isProcessing}
              variant="outline"
              className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-800"
              data-testid="srt-preview-btn"
            >
              {isProcessing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <CheckCircle className="w-4 h-4 mr-2" />}
              Visualizar
            </Button>
            <Button
              onClick={downloadExcel}
              disabled={files.length === 0 || isProcessing}
              className="flex-1 bg-teal-600 hover:bg-teal-700 text-white"
              data-testid="srt-download-btn"
            >
              {isProcessing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Download className="w-4 h-4 mr-2" />}
              Baixar Excel
            </Button>
          </div>

          {processedFiles.length > 0 && (
            <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-xl p-4" data-testid="srt-processed">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="w-4 h-4 text-emerald-400" />
                <span className="text-sm text-emerald-400 font-medium">Processado</span>
              </div>
              {processedFiles.map((f, i) => (
                <div key={`${f.ep_number}-${i}`} className="flex justify-between text-sm">
                  <span className="text-slate-400">{f.ep_number}</span>
                  <span className="text-white">{f.line_count} linhas</span>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="lg:col-span-7">
          <div className="bg-slate-800/50 border border-slate-700 rounded-xl p-4 h-full" data-testid="srt-preview-section">
            <div className="flex justify-between items-center mb-4">
              <span className="text-sm text-slate-400 font-medium">Preview da Planilha</span>
              {previewData.length > 0 && <span className="text-xs text-teal-400">{previewData.length} linhas</span>}
            </div>
            {previewData.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-16 text-center">
                <FileSpreadsheet className="w-14 h-14 text-slate-700 mb-3" />
                <p className="text-slate-500 text-sm">Adicione arquivos e clique em "Visualizar"</p>
              </div>
            ) : (
              <div className="max-h-[500px] overflow-auto border border-slate-700 rounded-lg">
                <table className="w-full text-sm" data-testid="srt-preview-table">
                  <thead className="sticky top-0 z-10">
                    <tr className="bg-slate-900 text-slate-400 text-xs uppercase tracking-wider">
                      <th className="px-3 py-2 text-left w-16">EP</th>
                      <th className="px-3 py-2 text-left w-16">Linha</th>
                      <th className="px-3 py-2 text-left w-44">Tempo</th>
                      <th className="px-3 py-2 text-left w-28">Personagem</th>
                      <th className="px-3 py-2 text-left">Texto</th>
                      <th className="px-3 py-2 text-left w-20">Obs.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.slice(0, 100).map((line, i) => (
                      <tr
                        key={`${line.ep_number}-${line.line_number}-${i}`}
                        className="border-t border-slate-800 hover:bg-slate-800/50"
                        data-testid={`srt-row-${i}`}
                      >
                        <td className="px-3 py-2 text-teal-400">{line.ep_number}</td>
                        <td className="px-3 py-2 text-white/80">{line.line_number}</td>
                        <td className="px-3 py-2 text-white/70 text-xs">{line.time}</td>
                        <td className="px-3 py-2 text-white/60">{line.character || "-"}</td>
                        <td className="px-3 py-2 text-white/90 truncate max-w-xs">{line.text}</td>
                        <td className="px-3 py-2 text-white/60">{line.observations || "-"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {previewData.length > 100 && (
                  <div className="text-center py-3 text-xs text-slate-500 border-t border-slate-700">
                    Mostrando 100 de {previewData.length} linhas
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};
